import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RelationshipGraph from '../components/relationships/RelationshipGraph';
import RelationshipFilters from '../components/relationships/RelationshipFilters';
import RelationshipStatistics from '../components/relationships/RelationshipStatistics';
import SuspiciousPatterns from '../components/relationships/SuspiciousPatterns';
import { relationshipService } from '../services/relationshipService';
import type { RelationshipGraph as GraphType, RelationshipFilters as FiltersType } from '../types/relationship';

const RelationshipAnalysisPage: React.FC = () => {
  const { guestId } = useParams<{ guestId: string }>();
  const navigate = useNavigate();
  
  const [graph, setGraph] = useState<GraphType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FiltersType>({
    maxDepth: 2,
    includeWeak: false,
    minStrength: 'WEAK'
  });

  useEffect(() => {
    if (guestId) {
      loadRelationshipGraph();
    }
  }, [guestId, filters]);

  const loadRelationshipGraph = async () => {
    if (!guestId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await relationshipService.getRelationshipGraph(guestId, filters);
      setGraph(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load relationship graph');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: FiltersType) => {
    setFilters(newFilters);
  };

  const handleExport = async (format: 'JSON' | 'CSV' | 'PNG') => {
    if (!guestId) return;

    try {
      const response = await relationshipService.exportGraph(guestId, format, filters);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'JSON' ? 'application/json' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export graph');
    }
  };

  const handleNodeClick = (nodeId: string) => {
    navigate(`/relationships/${nodeId}`);
  };

  if (!guestId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a guest to view relationships.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Relationship Analysis
        </h1>
        {graph && (
          <p className="text-gray-600">
            Analyzing relationships for {graph.center_guest.full_name}
          </p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6">
        <RelationshipFilters
          filters={filters}
          onChange={handleFilterChange}
          onExport={handleExport}
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Content */}
      {!loading && graph && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Graph */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Relationship Graph</h2>
              <RelationshipGraph
                graph={graph}
                onNodeClick={handleNodeClick}
              />
            </div>
          </div>

          {/* Statistics Sidebar */}
          <div className="space-y-6">
            <RelationshipStatistics guestId={guestId} />
            <SuspiciousPatterns guestId={guestId} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipAnalysisPage;
